import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import productoService, { PARAMS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { formatCurrency } from '../../utils/formatters';
import {
  mapProductoToRecetaArticulo,
  RecetaArticuloSeleccionado,
} from './RecetaArticuloSearchDialog';

const PAGE_SIZE = 20;
const DEBOUNCE_MS_DEFAULT = 200;

interface Props {
  value: string;
  /** Si el artículo ya está resuelto, no busca sugerencias hasta que el usuario edite el código. */
  articuloResuelto?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  resolving?: boolean;
  tiposArticulo?: string;
  /** 'venta' muestra precio neto de catálogo en sugerencias; default 'costo'. */
  modoPrecio?: 'costo' | 'venta';
  /** Label outlined (como TextField estándar). Si no se pasa, queda sin label (grillas densas). */
  label?: string;
  helperText?: React.ReactNode;
  size?: 'small' | 'medium';
  /** Debounce de sugerencias en ms (default 200). */
  debounceMs?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & Record<string, unknown>;
  sx?: object;
  onChange: (value: string) => void;
  onSelect: (item: RecetaArticuloSeleccionado) => void;
  onResolveExact: (codigo: string) => void;
  onOpenDialog: () => void;
  onKeyDownExtra?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const RecetaArticuloCodigoField: React.FC<Props> = ({
  value,
  articuloResuelto = false,
  disabled,
  readOnly,
  error,
  resolving,
  tiposArticulo,
  modoPrecio = 'costo',
  label,
  helperText,
  size = 'small',
  debounceMs = DEBOUNCE_MS_DEFAULT,
  inputProps,
  sx,
  onChange,
  onSelect,
  onResolveExact,
  onOpenDialog,
  onKeyDownExtra,
}) => {
  const [suggestions, setSuggestions] = useState<RecetaArticuloSeleccionado[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const selectingRef = useRef(false);

  const resetSuggestions = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscar = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed || readOnly || articuloResuelto) {
        resetSuggestions();
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setLoading(true);
      try {
        const res = await productoService.getProductos({
          searchTerm: trimmed,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          ...PARAMS_PRODUCTO_OPERATIVO,
          ...(tiposArticulo?.trim() ? { tiposArticulo: tiposArticulo.trim() } : {}),
        });
        if (requestId !== searchRequestIdRef.current) return;

        const items = (res.items ?? []).map(mapProductoToRecetaArticulo);
        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        itemRefs.current = [];
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        resetSuggestions();
      } finally {
        if (requestId === searchRequestIdRef.current) setLoading(false);
      }
    },
    [readOnly, articuloResuelto, tiposArticulo, resetSuggestions],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (articuloResuelto) {
      resetSuggestions();
      return;
    }
    debounceRef.current = setTimeout(() => void buscar(value), debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, articuloResuelto, buscar, resetSuggestions, debounceMs]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = (item: RecetaArticuloSeleccionado) => {
    selectingRef.current = true;
    searchRequestIdRef.current += 1;
    resetSuggestions();
    onSelect(item);
    setTimeout(() => {
      selectingRef.current = false;
    }, 200);
  };

  const handleBlur = () => {
    if (readOnly || articuloResuelto) return;
    setOpen(false);
    setTimeout(() => {
      if (selectingRef.current) return;
      const trimmed = value.trim();
      if (trimmed) onResolveExact(trimmed);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (readOnly) return;

    if (e.key === 'Tab') {
      onKeyDownExtra?.(e);
      return;
    }

    if (e.key === 'F4' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l')) {
      e.preventDefault();
      resetSuggestions();
      onOpenDialog();
      return;
    }

    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < 0) return 0;
          return prev < suggestions.length - 1 ? prev + 1 : 0;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < 0) return suggestions.length - 1;
          return prev > 0 ? prev - 1 : suggestions.length - 1;
        });
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
        const item = suggestions[idx];
        if (item) handleSelect(item);
        return;
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      resetSuggestions();
      if (!articuloResuelto) onResolveExact((e.target as HTMLInputElement).value);
      return;
    }

    onKeyDownExtra?.(e);
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) setOpen(false);
      }}
    >
      <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
        <TextField
          label={label}
          size={size}
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          inputProps={inputProps}
          error={error}
          helperText={helperText}
          disabled={disabled || resolving}
          InputProps={{
            readOnly,
            endAdornment: (
              <InputAdornment position="end">
                {resolving || loading ? (
                  <CircularProgress size={size === 'medium' ? 16 : 12} />
                ) : (
                  <IconButton
                    size="small"
                    edge="end"
                    disabled={readOnly || disabled}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      resetSuggestions();
                      onOpenDialog();
                    }}
                    aria-label="Buscar componente"
                  >
                    <SearchIcon fontSize={size === 'medium' ? 'small' : undefined} sx={size === 'small' ? { fontSize: 16 } : undefined} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={sx}
        />

        <Popper
          open={open && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400 }}
        >
          <Paper
            elevation={6}
            sx={{
              mt: 0.5,
              minWidth: Math.max(anchorRef.current?.clientWidth ?? 0, 360),
              maxWidth: 480,
              maxHeight: 280,
              overflowY: 'auto',
              borderRadius: 2,
            }}
          >
            <List dense disablePadding>
              {suggestions.map((s, idx) => (
                <ListItem key={`${s.codigoArticulo}-${idx}`} disablePadding divider>
                  <ListItemButton
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    selected={idx === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(s)}
                    sx={{ py: 0.75, px: 1.5, alignItems: 'flex-start', gap: 1 }}
                  >
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap>
                        {s.descripcion}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.25} flexWrap="wrap">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {s.codigoArticulo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.unidadMedida}
                        </Typography>
                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                          {formatCurrency(
                            modoPrecio === 'venta'
                              ? s.precioVenta ?? s.costo
                              : s.costo
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default RecetaArticuloCodigoField;
